<template>
  <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:max-w-full">
    <!-- UNSTACKED FILTER CHIPS (isUnstacked === true) -->
    <template v-if="unstackedFilters.length">
      <template v-for="f in unstackedFilters" :key="f.name">
        <Popover v-model:open="filterPopoverStates[f.name]">
          <template v-if="getAppliedFilterForUnstacked(f)">
            <PopoverTrigger as-child>
              <div
                class="tw:border tw:flex tw:items-center tw:gap-2 tw:pl-1.5 tw:pr-2 tw:py-1 tw:rounded-full tw:text-sm tw:group tw:cursor-default tw:hover:bg-zinc-50 tw:overflow-hidden tw:truncate tw:max-w-full"
                role="button" tabindex="0"
                :aria-label="$t('Edit filter') + ': ' + f.label">
                <button v-on:click.stop="removeFilter(f)"
                        class="tw:flex tw:items-center tw:justify-center tw:hover:text-red-600"
                        :aria-label="$t('Remove all filters') + ': ' + f.label">
                  <CircleX class="tw:size-4.5" />
                </button>

                <dl class="tw:flex tw:items-center tw:overflow-hidden">
                  <dt class="tw:text-zinc-700 tw:whitespace-nowrap">
                    {{ f.label }}
                  </dt>
                  <dd aria-hidden="true" class="tw:border-l tw:border-zinc-300 tw:mx-2 tw:h-4"></dd>
                  <dd class="tw:text-blue-600 tw:flex-1 tw:min-w-0 tw:whitespace-nowrap tw:truncate">
                    {{ getSelectedFilterLabels(getAppliedFilterForUnstacked(f)!) }}
                  </dd>
                </dl>
                <ChevronDown class="tw:-ml-0.5 tw:size-3.5 tw:text-zinc-700" />
              </div>
            </PopoverTrigger>
          </template>
          <template v-else>
            <PopoverTrigger as-child>
              <button
                class="tw:border tw:border-dashed tw:inline-flex tw:items-center tw:gap-2 tw:pl-1.5 tw:pr-3 tw:py-1 tw:rounded-full tw:hover:bg-zinc-50 tw:text-sm tw:whitespace-nowrap"
                :aria-label="$t('Add filter') + ': ' + f.label"
              >
                <CirclePlus class="tw:size-4.5" />
                {{ f.label }}
              </button>
            </PopoverTrigger>
          </template>
          <PopoverContent align="start" class="tw:w-80 tw:space-y-4">
            <FilterForm
              :filter-definition="f"
              :filter-value="getFilterValueForFilterForm(f)"
              :legend="$t('Filter by {type}', { type: f.label.toLowerCase() })"
              @setValue="onUnstackedSetValue(f, $event)"
            />
          </PopoverContent>
        </Popover>
      </template>
    </template>

    <!-- STACKED FILTERS (appliedStackedFilters only) -->
    <template v-if="appliedStackedFilters.length">
      <template v-for="appliedFilterWithValue in appliedStackedFilters" :key="appliedFilterWithValue.filter.name">
        <Popover v-model:open="filterPopoverStates[appliedFilterWithValue.filter.name]">
          <PopoverTrigger as-child>
            <div
              class="tw:border tw:flex tw:items-center tw:gap-2 tw:pl-1.5 tw:pr-2 tw:py-1 tw:rounded-full tw:text-sm tw:group tw:cursor-default tw:hover:bg-zinc-50 tw:overflow-hidden tw:truncate tw:max-w-full"
              role="button" tabindex="0"
              :aria-label="$t('Edit filter') + ': ' + appliedFilterWithValue.filter.label">
              <button v-on:click.stop="removeFilter(appliedFilterWithValue.filter)"
                class="tw:flex tw:items-center tw:justify-center tw:hover:text-red-600"
                :aria-label="$t('Remove all filters') + ': ' + appliedFilterWithValue.filter.label">
                <CircleX class="tw:size-4.5" />
              </button>

              <dl class="tw:flex tw:items-center tw:overflow-hidden">
                <dt class="tw:text-zinc-700 tw:whitespace-nowrap">
                  {{ appliedFilterWithValue.filter.label }}
                </dt>
                <dd aria-hidden="true" class="tw:border-l tw:border-zinc-300 tw:mx-2 tw:h-4"></dd>
                <dd class="tw:text-blue-600 tw:flex-1 tw:min-w-0 tw:whitespace-nowrap tw:truncate">
                  {{ getSelectedFilterLabels(appliedFilterWithValue) }}
                </dd>
              </dl>
              <ChevronDown class="tw:-ml-0.5 tw:size-3.5 tw:text-zinc-700" />
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" class="tw:w-80">
            <FilterForm
              :filter-definition="appliedFilterWithValue.filter"
              :filter-value="appliedFilterWithValue.value"
              :legend="$t('Filter by {type}', { type: appliedFilterWithValue.filter.label.toLowerCase() })"
              @setValue="updateFilter(appliedFilterWithValue.filter, $event)"
            />
          </PopoverContent>
        </Popover>
      </template>
    </template>

    <!-- ADD FILTER BUTTON (stacked only) -->
    <Popover v-model:open="isAddFilterPopoverOpen">
      <PopoverTrigger as-child>
        <button
          class="tw:border tw:border-dashed tw:inline-flex tw:items-center tw:gap-2 tw:pl-1.5 tw:pr-3 tw:py-1 tw:rounded-full tw:hover:bg-zinc-50 tw:text-sm tw:whitespace-nowrap"
          :disabled="!hasUnselectedStackedFilters"
          :class="{ 'tw:opacity-50 tw:cursor-not-allowed': !hasUnselectedStackedFilters }"
          @click="!hasUnselectedStackedFilters && (isAddFilterPopoverOpen = false)">
          <CirclePlus class="tw:size-4.5" /> {{ $t('Add filter') }}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" class="tw:w-80 tw:space-y-4" @openAutoFocus="onOpenAutoFocus">
        <div>
          <label for="filter-type-select" class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
            {{ $t('Filter type') }}
          </label>
          <select ref="filterTypeSelect" id="filter-type-select" v-model="newFilterName" class="form-control tw:w-full">
            <option value=""></option>
            <option v-for="availableFilter in unselectedStackedFilters" :key="availableFilter.name" :value="availableFilter.name">
              {{ availableFilter.label }}
            </option>
          </select>
        </div>
        <LoadingOverlay :active="isModelOptionsLoading">
          <div v-if="filterType && filterType.name">
            <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
              {{ $t('Filter values') }}
            </label>
            <FilterForm
              :filter-definition="filterType"
              @setValue="addFilter(filterType, $event)"
            />
          </div>
          <div v-else class="tw:flex tw:justify-start tw:gap-2">
            <button type="submit" class="btn btn-primary tw:w-full" disabled>
              {{ $t('Apply') }}
            </button>
          </div>
        </LoadingOverlay>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch, watchEffect } from "vue";
import { ChevronDown, CirclePlus, CircleX } from "@lucide/vue";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/components/ui/popover";
import FilterForm from "./FilterForm.vue";
import { type FilterDefinition, FilterDefinitionTypeEnum, type FilterWithValue } from "./filters";
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import {
  getFilterRawValue,
  getFilterValueForForm,
  parseAppliedFilters,
  serializeFilterValue,
  isValidFilterValue
} from './composables/useFilterValue';
import { useFilterLabels } from './composables/useFilterLabels';
import { useAsyncFilterOptions } from './composables/useAsyncFilterOptions';

const { getSelectedFilterLabels } = useFilterLabels()

const props = withDefaults(defineProps<{
  availableFilters: FilterDefinition[],
  baseFilters: Record<string, any>,
  modelValue: Record<string, string | boolean>
}>(), {
  baseFilters: () => ({}),
})

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, string | boolean>]
}>()

const availableFiltersRef = ref<FilterDefinition[]>(props.availableFilters)
watchEffect(() => {
  availableFiltersRef.value = props.availableFilters.map(f => ({
    ...f,
    // Support both camelCase (isUnstacked) and snake_case (is_unstacked) from backend
    isUnstacked: f.isUnstacked ?? (f as any).is_unstacked ?? false,
  }))
})

// Reactive state
const filters = ref(props.modelValue)
const newFilterName = ref<string>('')
const filterType = ref<FilterDefinition | undefined>(undefined)
const isAddFilterPopoverOpen = ref<boolean>(false)
const filterPopoverStates = ref<Record<string, boolean>>({})

// Async filter options loading
const { isLoading: isModelOptionsLoading, loadAsyncOptions } = useAsyncFilterOptions(props.baseFilters)

// --- Computed for stacked/unstacked separation ---
const isUnstacked = (f: FilterDefinition) => f.isUnstacked ?? false

const unstackedFilters = computed(() =>
  availableFiltersRef.value.filter(isUnstacked)
)

const stackedFilters = computed(() =>
  availableFiltersRef.value.filter(f => !isUnstacked(f))
)

const appliedFilters = computed<FilterWithValue[]>(() =>
  parseAppliedFilters(filters.value, availableFiltersRef.value)
)

const appliedStackedFilters = computed<FilterWithValue[]>(() =>
  appliedFilters.value.filter(af => !isUnstacked(af.filter))
)

const unselectedStackedFilters = computed(() =>
  stackedFilters.value.filter(f => !Object.keys(filters.value).includes(f.name))
)

const hasUnselectedStackedFilters = computed(() =>
  unselectedStackedFilters.value.length > 0
)


// Sync with v-model
watch(() => props.modelValue, (val) => {
  filters.value = val
}, { deep: true })

watch(filters, (val) => {
  emit('update:modelValue', val)
}, { deep: true })

// Reset form when popover closes
watch(isAddFilterPopoverOpen, (isOpen) => {
  if (!isOpen) {
    newFilterName.value = ''
    filterType.value = undefined
  }
})

// --- Helpers for filter values ---
function getFilterValueForFilterForm(filter: FilterDefinition): string[] | boolean | string | null {
  const raw = getFilterRawValue(filters.value, filter.name)
  return getFilterValueForForm(filter, raw)
}

function getAppliedFilterForUnstacked(filter: FilterDefinition): FilterWithValue | undefined {
  return appliedFilters.value.find(af => af.filter.name === filter.name)
}

// --- Filter mutations ---
function addFilter(filter: FilterDefinition, filterValue: string[] | boolean | string): void {
  if (!isValidFilterValue(filter, filterValue)) {
    return
  }

  filters.value[filter.name] = serializeFilterValue(filter, filterValue)

  // Reset form
  newFilterName.value = ''
  isAddFilterPopoverOpen.value = false
}

function removeFilter(filter: FilterDefinition): void {
  delete filters.value[filter.name]
}

function updateFilter(filter: FilterDefinition, filterValue: string[] | boolean | string): void {
  if (!isValidFilterValue(filter, filterValue)) {
    delete filters.value[filter.name]
  } else {
    filters.value[filter.name] = serializeFilterValue(filter, filterValue)
  }

  // Close popover
  filterPopoverStates.value[filter.name] = false
}

function onUnstackedSetValue(filter: FilterDefinition, value: string[] | boolean | string): void {
  if (filters.value[filter.name] != null) {
    updateFilter(filter, value)
  } else {
    addFilter(filter, value)
    filterPopoverStates.value[filter.name] = false
  }
}

// --- Load async filter options ---
async function loadFilterOptions(filter: FilterDefinition): Promise<void> {
  if (
    filter.type !== FilterDefinitionTypeEnum.ASYNC_SELECT ||
    (filter.options && filter.options.length > 0)
  ) {
    return
  }

  const options = await loadAsyncOptions(filter)
  const index = availableFiltersRef.value.findIndex(f => f.name === filter.name)
  if (index !== -1) {
    availableFiltersRef.value[index] = {
      ...availableFiltersRef.value[index],
      options: options
    }
  }
}

// Watch new filter selection and load options if needed
watch(newFilterName, async (newValue) => {
  const index = availableFiltersRef.value.findIndex(f => f.name === newValue)
  if (index !== -1) {
    await loadFilterOptions(availableFiltersRef.value[index])
    filterType.value = availableFiltersRef.value[index]
  }
})

// Load options for already applied filters
watch(appliedFilters, async (filtersArr) => {
  for (const filterWithValue of filtersArr) {
    await loadFilterOptions(filterWithValue.filter)
  }
}, { immediate: true })

/**
 * This function is used to focus the filter type select element when the popover opens on iOS devices.
 * It prevents the default behavior of the popover opening, which can cause issues with focus.
 * https://github.com/nineteen-2/digitaltestcenter-backend/pull/3050
 */
const filterTypeSelect = useTemplateRef('filterTypeSelect')

function onOpenAutoFocus(e: Event): void {
  if (!(/iPhone|iPad|iPod/.test(navigator.userAgent) && /WebKit/.test(navigator.userAgent))) {
    return
  }

  e.preventDefault()

  setTimeout(() => {
    filterTypeSelect.value?.focus()
  }, 1)
}
</script>